import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  getDocumentType,
  createDocumentType,
  updateDocumentType
} from 'helpers/documentManagerApi';
import FieldBuilder, { RESERVED_KEYS } from './FieldBuilder';
import DynamicFieldRenderer from '../fields/DynamicFieldRenderer';
import evaluateConditions from 'helpers/evaluateConditions';
import { slugifyText } from 'helpers/utils';

const DocumentTypeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = id !== undefined && id !== 'create';

  const [name, setName] = useState('');
  const [fileMode, setFileMode] = useState('multiple');
  const [sortOrder, setSortOrder] = useState(0);
  const [fields, setFields] = useState([]);
  const [originalFields, setOriginalFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Preview state
  const [previewValues, setPreviewValues] = useState({});

  // Confirmation Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      const fetchTypeDetail = async () => {
        try {
          setLoading(true);
          const res = await getDocumentType(id);
          if (res && res.status) {
            const data = res.data;
            setName(data.name || '');
            setFileMode(data.file_mode || 'multiple');
            setSortOrder(data.sort_order || 0);

            // Standardize fields
            const fetchedFields = (data.fields || [])
              .map((f, idx) => ({
                ...f,
                sort_order: f.sort_order || idx + 1
              }))
              .sort((a, b) => a.sort_order - b.sort_order)
              .filter(ele => ele.is_active);

            setFields(fetchedFields);
            setOriginalFields(JSON.parse(JSON.stringify(fetchedFields)));
          } else {
            toast.error(
              res?.message || 'Failed to fetch document type details'
            );
            navigate('/main/admin/document-types');
          }
        } catch (err) {
          console.error(err);
          toast.error('Error fetching document type details');
          navigate('/main/admin/document-types');
        } finally {
          setLoading(false);
        }
      };

      fetchTypeDetail();
    }
  }, [id, isEditMode, navigate]);

  const handlePreviewChange = (fieldKey, value) => {
    setPreviewValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const validateSchema = () => {
    if (!name.trim()) {
      toast.error('Document Type Name is required');
      return false;
    }

    // Validate fields
    if (fields.length > 0) {
      const keys = fields.map(f => f.field_key.trim());

      // Duplicates
      const duplicates = keys.filter(
        (item, index) => keys.indexOf(item) !== index
      );
      if (duplicates.length > 0) {
        toast.error(`Duplicate field keys found: ${duplicates.join(', ')}`);
        return false;
      }

      // Empty keys
      if (keys.some(k => k === '')) {
        toast.error('All field keys must have a valid value.');
        return false;
      }

      // Reserved words
      const reserved = keys.filter(k => RESERVED_KEYS.includes(k));
      if (reserved.length > 0) {
        toast.error(
          `Reserved words cannot be used as field keys: ${reserved.join(', ')}`
        );
        return false;
      }

      // Select / Radio options check
      for (const field of fields) {
        if (['select', 'radio'].includes(field.type)) {
          if (!field.options || field.options.length === 0) {
            toast.error(
              `Field "${field.label}" of type "${field.type}" must have at least one option.`
            );
            return false;
          }
        }

        if (field.conditions?.rules?.length) {
          for (const rule of field.conditions.rules) {
            if (!rule.field_key || !rule.operator) {
              toast.error(`Condition on "${field.label}" is incomplete.`);
              return false;
            }
            if (
              !['filled', 'empty'].includes(rule.operator) &&
              (rule.value === undefined ||
                rule.value === null ||
                rule.value === '')
            ) {
              toast.error(
                `Condition on "${field.label}" needs an expected value.`
              );
              return false;
            }
          }
        }
      }
    }

    return true;
  };

  const hasSchemaChanged = () => {
    if (!isEditMode) return false;
    // Compare structural properties of fields
    if (fields.length !== originalFields.length) return true;
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const o = originalFields[i];
      if (
        f.field_key !== o.field_key ||
        f.label !== o.label ||
        f.type !== o.type ||
        f.required !== o.required ||
        JSON.stringify(f.options) !== JSON.stringify(o.options) ||
        JSON.stringify(f.conditions || null) !==
          JSON.stringify(o.conditions || null) ||
        f.sort_order !== o.sort_order
      ) {
        return true;
      }
    }
    return false;
  };

  const handleSubmit = e => {
    if (e) e.preventDefault();

    if (!validateSchema()) return;

    if (isEditMode && hasSchemaChanged()) {
      // Show confirmation dialog before schema version bump
      setShowConfirmModal(true);
    } else {
      saveDocumentType();
    }
  };

  const saveDocumentType = async () => {
    try {
      setSaving(true);
      setShowConfirmModal(false);

      const cleanedFields = fields.map((field, index) => ({
        field_key: field.field_key,
        label: field.label,
        type: field.type,
        required: !!field.required,
        options: field.options || [],
        sort_order: field.sort_order || index + 1,
        conditions: field.conditions?.rules?.length
          ? {
              action: 'show',
              logic: field.conditions.logic || 'AND',
              rules: field.conditions.rules.map(rule => ({
                field_key: rule.field_key,
                operator: rule.operator,
                value: rule.value
              }))
            }
          : null
      }));

      const payload = {
        name,
        slug: slugifyText(name),
        file_mode: fileMode,
        sort_order: Number(sortOrder),
        fields: cleanedFields
      };

      let res;
      if (isEditMode) {
        res = await updateDocumentType(id, payload);
      } else {
        res = await createDocumentType(payload);
      }

      if (res && res.status) {
        toast.success(
          `Document type ${isEditMode ? 'updated' : 'created'} successfully`
        );
        navigate('/main/admin/document-types');
      } else {
        toast.error(res?.message || 'Failed to save document type');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error saving document type');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 text-center">
        <h5>Loading Document Type Builder...</h5>
      </Card>
    );
  }

  return (
    <Row>
      <Col lg={8}>
        <Card className="mb-3">
          <Card.Header className="bg-light">
            <h5 className="mb-0">
              {isEditMode
                ? `Edit Document Type: ${name}`
                : 'Create Document Type'}
            </h5>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="typeName">
                    <Form.Label className="fw-semi-bold">
                      Document Type Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="e.g. Lab Report"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semi-bold">File Mode</Form.Label>
                    <div>
                      <Form.Check
                        type="radio"
                        label="Single File"
                        name="fileMode"
                        id="fileModeSingle"
                        checked={fileMode === 'single'}
                        onChange={() => setFileMode('single')}
                        inline
                      />
                      <Form.Check
                        type="radio"
                        label="Multiple Files"
                        name="fileMode"
                        id="fileModeMultiple"
                        checked={fileMode === 'multiple'}
                        onChange={() => setFileMode('multiple')}
                        inline
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="sortOrder">
                    <Form.Label className="fw-semi-bold">Sort Order</Form.Label>
                    <Form.Control
                      type="number"
                      value={sortOrder}
                      onChange={e => setSortOrder(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr />

              <FieldBuilder
                fields={fields}
                onChange={setFields}
                isEditMode={isEditMode}
              />

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate('/main/admin/document-types')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Document Type'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Col>

      {/* Live Preview Column */}
      <Col lg={4}>
        <Card className="sticky-top" style={{ top: '80px', zIndex: 10 }}>
          <Card.Header className="bg-light">
            <h6 className="mb-0">Live Form Preview</h6>
          </Card.Header>
          <Card.Body
            style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}
          >
            <div className="border rounded p-3 bg-card mb-3">
              <h5 className="fs-9 mb-1">New Document</h5>
              <p className="text-500 fs-11 mb-3">
                Renders exact fields matching the type definition.
              </p>

              <Form.Group className="mb-3" controlId="previewTitle">
                <Form.Label className="fw-semi-bold">
                  Document Title <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Title of the document"
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="previewDesc">
                <Form.Label className="fw-semi-bold">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Optional notes"
                  disabled
                />
              </Form.Group>

              {fields.map((field, index) => {
                const previewKey = field.field_key || `field_${index}`;
                const previewField = {
                  ...field,
                  field_key: previewKey,
                  label: field.label || `Field Label ${index + 1}`
                };
                const isVisible = evaluateConditions(
                  field.conditions,
                  previewValues
                );

                if (!isVisible) {
                  return (
                    <div
                      key={index}
                      className="border rounded bg-light text-400 px-3 py-2 mb-3 fs-11"
                    >
                      <FontAwesomeIcon icon="eye" className="me-2" />
                      {previewField.label} is hidden until its condition is met.
                    </div>
                  );
                }

                return (
                  <DynamicFieldRenderer
                    key={index}
                    field={previewField}
                    value={previewValues[previewKey]}
                    onChange={val => handlePreviewChange(previewKey, val)}
                  />
                );
              })}

              <div className="border border-dashed rounded p-3 text-center bg-light text-500 fs-10">
                <FontAwesomeIcon
                  icon="file-upload"
                  className="fs-5 mb-2 text-primary"
                />
                <div>Drag & drop files here</div>
                <div className="fs-11 text-400 mt-1">
                  Accepts PDF, JPG, PNG, TIFF, DOC (
                  {fileMode === 'single' ? 'Max 1 file' : 'Max 10 files'})
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-9">Confirm Schema Update</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs-10">
          <div className="alert alert-warning mb-0">
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            <strong>Important:</strong> You are changing the parameters of this
            document type. Existing documents created with the older schema will
            display a{' '}
            <em>&quot;schema updated since document was created&quot;</em>{' '}
            warning banner. Unknown legacy field values will remain read-only.
          </div>
          <p className="mt-3 mb-0">
            Are you sure you want to apply these changes and bump the schema
            version?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowConfirmModal(false)}
          >
            Go Back
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={saveDocumentType}
            disabled={saving}
          >
            Confirm & Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default DocumentTypeForm;
